import { defineComponent, h, nextTick } from 'vue';
import { QBtn, QIcon } from 'quasar';
import utils from '../../../Shared/utils.jsx';
//import formAnyOf from './anyof.jsx';
import methods from '../../../Shared/methods';
import EventBus from '../../event-bus.js';
import { Container, Draggable } from 'vue3-smooth-dnd';

import PropertyGrid from './PropertyGrid.jsx';
import AceEditor from '../3rdParty/AceEditor.jsx';

const FormArrayLabel = defineComponent({
    name: 'form-array-label',
    data: function () {
        return {
            titleexpn: null
        }
    },
    async mounted() {
        if (this.cmodel.$typeSchema) {
            const schema = await utils.schema.get(this.cmodel.$typeSchema);
            const res = utils.schema.resolve_Of(schema);

            // {{ActionExpression ? '?(' + ActionExpression.substring(0,8) + (ActionExpression.length>8?'...':'') + ') ' : ''}} {{ActionData.Expression}}
            if (res.documentEditorTitle) {
                // Build a proxy to enable the title expression to refer to missing elements in the model without throwing an exception
                const handler1 = {
                    has: function (oTarget, sKey) {
                        return sKey !== '_self';
                    },
                    get: function (target, prop, receiver) {
                        return prop in target ? target[prop] : null;
                    }
                };
                this.model_proxy = new Proxy(this.cmodel, handler1);

                this.titleexpn = utils.compile(this, res.documentEditorTitle);
            }
        }
    },
    computed: {
        title: function () {
            if (this.titleexpn)
                try {
                    return utils.evaluate(this.titleexpn, this.model_proxy);
                }
                catch (e) {
                    utils.warn('Array documentEditorTitle failed to evaluate: ' + this.titleexpn + '; ' + e);
                    return null;
                }
            else
                return null;
        },
    },
    props: {
        cmodel: null,
        visible: false,
    },
    render() {
        const style = {
            //display: "inline",
            fontFamily: "Arial",
            fontSize: "small", // x-small before
            overflow: "hidden",
            whiteSpace: "nowrap",
            maxWidth: "350px",
            marginTop: "2px",
            marginBottom: "1px",
        };
        return <div title={this.title} style={style}>{this.title}</div>;
    }
});

function toggleVisible(control, key) {
    const default_value = 'true';
    const value = control.visible_keys[key] || localStorage.getItem(key);

    if (!value) {
        // No setting, save the opposite of the default_value
        const new_value = default_value === 'true' ? 'false' : 'true';
        localStorage.setItem(key, new_value);
        control.visible_keys[key] = new_value;
        utils.log('Saved ' + key + ' as ' + new_value);
    }
    else {
        const new_value = value === 'true' ? 'false' : 'true';
        if (new_value === default_value) {
            // When the value is the default, remove the entry
            localStorage.removeItem(key);
            delete control.visible_keys[key];
            utils.log('Deleted ' + key);
        }
        else {
            // Otherwise, we must store the value
            localStorage.setItem(key, new_value);
            control.visible_keys[key] = new_value;
            utils.log('Saved ' + key + ' as ' + new_value);
        }
    }
}

function getVisibility(control, key) {
    const default_value = 'true';
    let value = control.visible_keys[key];
    if (!value) {
        value = localStorage.getItem(key);
        if (value)
            control.visible_keys[key] = value;
    }

    if (!value)
        return default_value === 'true';
    else
        return value === 'true';
}

export default defineComponent({
    name: 'form-array',
    components: {
        QBtn,
        QIcon,
        AceEditor,
        PropertyGrid,
        FormArrayLabel,
    },
    data: function () {
        return {
            selected:[],
            visible_keys: {},

            uniquekey: null,
            asjson: false,
            rawJsonEditorHeight: '100px',
            itemRowKeys: [],
        }
    },
    created() {
        this.uniquekey = utils.generateUUID();
    },
    mounted() {
        nextTick(() => this.ensureRawJsonAceResizeObserver());
    },
    updated() {
        nextTick(() => this.ensureRawJsonAceResizeObserver());
    },
    beforeUnmount() {
        this.teardownRawJsonAceResizeObserver();
    },
    computed: {
        ...utils.forms.computed,
        anySelected: function () {
            if (!this.selected || this.selected.length == 0) return false;
            for (let i = 0; i < this.selected.length; i++)
                if (this.selected[i]) return true;
            return false;
        }
    },
    methods: {
        ...methods,
        ensureRawJsonAceResizeObserver() {
            if (typeof ResizeObserver === 'undefined')
                return;
            const host = this.$refs.arrayRawJsonAceHost;
            if (!host) {
                this.teardownRawJsonAceResizeObserver();
                return;
            }
            if (this._arrayRawJsonResizeObserved === host)
                return;
            this.teardownRawJsonAceResizeObserver();
            this._arrayRawJsonResizeObserved = host;
            this._arrayRawJsonResizeObserver = new ResizeObserver(() => {
                const px = Math.round(host.offsetHeight || 0);
                if (!px)
                    return;
                const next = `${px}px`;
                if (this.rawJsonEditorHeight !== next)
                    this.rawJsonEditorHeight = next;
            });
            this._arrayRawJsonResizeObserver.observe(host);
        },
        teardownRawJsonAceResizeObserver() {
            if (this._arrayRawJsonResizeObserver) {
                this._arrayRawJsonResizeObserver.disconnect();
                this._arrayRawJsonResizeObserver = null;
            }
            this._arrayRawJsonResizeObserved = null;
        },
        ensureItemRowKeys(length) {
            while (this.itemRowKeys.length < length) {
                this.itemRowKeys.push(utils.generateUUID());
            }
            if (this.itemRowKeys.length > length) {
                this.itemRowKeys.length = length;
            }
        },
        resetItemRowKeys(length) {
            this.itemRowKeys = [];
            this.ensureItemRowKeys(length);
        },
        getItemRowKey(i, item) {
            if (item && typeof item === 'object' && item.$objectId != null) {
                return String(item.$objectId);
            }
            return this.itemRowKeys[i] || 'row_' + i;
        },
        arrayChromeFlags() {
            return utils.forms.getArrayChromeFlags({
                fieldSchema: this.schema,
            });
        },
        async addAnother() {
            if (!this.arrayChromeFlags().allowAdd)
                return;

            if (!this.cmodel[this.schemakey])
                this.cmodel[this.schemakey] = [];

            //const s = await utils.schema.resolve(this.schema.items);
            const schema = utils.schema.resolve_Of(this.schema.items);
            const newmodel = utils.schema.createDefaultModel(schema, this.root);
            this.cmodel[this.schemakey].push(newmodel);
            this.itemRowKeys.push(utils.generateUUID());
        },
        removeItem(index) {
            if (!this.arrayChromeFlags().allowRemove)
                return;

            this.cmodel[this.schemakey].splice(index, 1);
            this.itemRowKeys.splice(index, 1);
        },
        moveItemUp(index) {
            if (!this.arrayChromeFlags().allowMove)
                return;

            if (index > 0)
            {
                const item = this.cmodel[this.schemakey][index];
                this.cmodel[this.schemakey].splice(index, 1);
                this.cmodel[this.schemakey].splice(index - 1, 0, item);
                const key = this.itemRowKeys[index];
                this.itemRowKeys.splice(index, 1);
                this.itemRowKeys.splice(index - 1, 0, key);
            }
        },
        moveItemDown(index) {
            if (!this.arrayChromeFlags().allowMove)
                return;

            if (index < this.cmodel[this.schemakey].length - 1) {
                const item = this.cmodel[this.schemakey][index];
                this.cmodel[this.schemakey].splice(index, 1);
                this.cmodel[this.schemakey].splice(index + 1, 0, item);
                const key = this.itemRowKeys[index];
                this.itemRowKeys.splice(index, 1);
                this.itemRowKeys.splice(index + 1, 0, key);
            }
        },

        setSelection(index, checked) {
            while (index >= this.selected.length)
                this.selected.push(false);

            this.selected[index] = checked;
        },
        copy() {
            const controls = this.cmodel[this.schemakey];
            const selected = [];
            for (let i = 0; i < this.selected.length; i++)
                if (this.selected[i])
                    selected.push(controls[i]);

            localStorage.setItem('careClipboard', JSON.stringify(selected));
        },
        cut() {
            if (!this.arrayChromeFlags().allowRemove)
                return;

            const controls = this.cmodel[this.schemakey];
            const selected = [];
            for (let i = 0; i < this.selected.length; i++)
                if (this.selected[i])
                    selected.push(controls[i]);

            localStorage.setItem('careClipboard', JSON.stringify(selected));

            for (let i = this.selected.length - 1; i >= 0; i--) {
                if (this.selected[i]) {
                    controls.splice(i, 1);
                    this.itemRowKeys.splice(i, 1);
                }
            }

            this.selected = [];
        },
        paste() {
            if (!this.arrayChromeFlags().allowAdd)
                return;

            const rawbuffer = localStorage.getItem('careClipboard');
            if (rawbuffer && typeof rawbuffer === 'string')
            {
                try {
                    const data = JSON.parse(rawbuffer);
                    if (typeof data === 'object' && Array.isArray(data)) {
                        if (!this.cmodel[this.schemakey])
                            this.cmodel[this.schemakey] = [];

                        const controls = this.cmodel[this.schemakey];
                        for (let i = 0; i < data.length; i++)
                        {
                            if ('$objectId' in data[i]) data[i].$objectId = utils.generateUUID();
                            controls.push(data[i]);
                            this.itemRowKeys.push(utils.generateUUID());
                        }
                    }
                }
                catch (e)
                {
                    alert('Failed to parse data from clipboard buffer: ' + e);
                }
            }
        },
        selectAll() {
            const controls = this.cmodel[this.schemakey];

            this.selected = new Array(controls.length);

            for (let i = 0; i < this.selected.length; i++)
                this.selected[i] = true;
        },
        selectNone() {
            this.selected = [];
        },
        extractor(control, prop, index) {
            const accept = async (value) => {
                const newmodel = control.cmodel[index];
                let newcontrol;
                let replaced;
                let type;
                switch (this.schema.items.Id) {
                    case '/Apps/Schema/AnyOf/schema/public/Platform.Schema.BaseTypes.v1/DynamicControl':
                        // Build a new user control and embed a reference in place of control.cmodel[index]
                        type = 'UserControl';
                        newcontrol = {
                            $typeSchema: '/schema/public/Platform.Schema.Documents.v1/Document_UserControl',
                            Name: value,
                            Data: newmodel,
                            Public: false,
                            ControlDataSchema: {
                                type: 'object',
                                additionalProperties: true,
                                properties: {},
                            },
                            $DeployID: 'dev',
                        };
                        replaced = {
                            $typeSchema: '/schema/public/Platform.Schema.DynamicControls.v1/DynamicControl_Reference',
                            PrimitiveControl: false,
                            CacheControl: true,
                            ControlData: {},
                            ControlURL: newcontrol.Name,
                            $objectId: utils.generateUUID(),
                        };
                        break;

                    case '/Apps/Schema/AnyOf/schema/public/Platform.Schema.BaseTypes.v1/UIAction':
                        // Build a new user action and embed a reference in place of control.cmodel[index]
                        type = 'Action';
                        newcontrol = {
                            $typeSchema: '/schema/public/Platform.Schema.Documents.v1/Document_UserAction',
                            Name: value,
                            Data: newmodel,
                            Public: false,
                            ActionDataSchema: {
                                type: 'object',
                                additionalProperties: true,
                                properties: {},
                            },
                            $DeployID: 'dev',
                        };
                        replaced = {
                            $typeSchema: '/schema/public/Platform.Schema.UIActions.v1/UIAction_Reference',
                            PrimitiveAction: true,
                            ActionType: 'ReferenceUserAction',
                            ActionData: {
                                UserAction: {
                                    ActionData: {},
                                    ActionURL: newcontrol.Name,
                                    CacheAction: true,
                                }
                            },
                            RunAsync: false,
                            $objectId: utils.generateUUID(),
                        };
                        break;

                    default:
                        return;
                }

                if (this.root.saveExtractedEntity) {
                    // Save the new control or action
                    const success = await this.root.saveExtractedEntity(type, newcontrol);

                    // Replace the current item with the new content which points to the newly saved entity
                    if (success)
                        control.cmodel[index] = replaced;
                }
            };

            let title;
            let label;
            switch (this.schema.items.Id) {
                case '/Apps/Schema/AnyOf/schema/public/Platform.Schema.BaseTypes.v1/DynamicControl':
                    title = 'Extract User Control';
                    label = 'New User Control Name';
                    break;

                case '/Apps/Schema/AnyOf/schema/public/Platform.Schema.BaseTypes.v1/UIAction':
                    title = 'Extract User Action';
                    label = 'New User Action Name';
                    break;

                default:
                    return;
            }

            EventBus.$emit('SHOW_DIALOG', (
                <input-dialog
                    root={this.root}
                    title={title}
                    label={label}
                    accept={accept}>
                </input-dialog>
            ));
        },
        reOrderItems(result) {
            if (!this.arrayChromeFlags().allowMove)
                return;
            if (result.removedIndex == null || result.addedIndex == null) return;
            const cmodel = this.cmodel[this.schemakey] || [];
            const item = cmodel[result.removedIndex];
            cmodel.splice(result.removedIndex, 1);
            cmodel.splice(result.addedIndex, 0, item);
            const key = this.itemRowKeys[result.removedIndex];
            this.itemRowKeys.splice(result.removedIndex, 1);
            this.itemRowKeys.splice(result.addedIndex, 0, key);
        },

        sync(text) {
            try {
                const parsed = JSON.parse(text);
                this.cmodel[this.schemakey] = parsed;
                this.resetItemRowKeys(Array.isArray(parsed) ? parsed.length : 0);
            }
            catch (e) {
                utils.warn('array in propertygrid could not parse JSON', e);
            }
        },
    },
    props: {
        name: '',
        schemakey: '',
        root: null,
        schema: null,
        cmodel: null,
        extra: null,
        readonly: false,
        path: null,
        child: 0,
        navigateTo: null,
        canNavigate: false,
        propertygrid: false,
    },
    render() {
        if (!this.Condition || !this.schema || !this.schema.items)
            return null;

        let desc;
        if (this.schema.description)
            desc = <i class="mdi mdi-information" title={this.schema.description} style={{ color: "var(--v-checkboxGrey-base)", marginLeft: "3px" }}></i>;

        let err;
        if (this.schema.formatData?.error)
            err = utils.tt(this.schema.formatData.error, <QIcon name="mdi-alert" size="xs" color="negative" />);

        const schema = utils.schema.resolve_Of(this.schema.items);
        const name = <span style={{ marginLeft: (this.child * 15) + "px" }}>{this.name}</span>;

        const heads = [];
        const foots = [];
        let toolbarRow = null;
        const draggableChildren = [];
        const cmodel = this.cmodel[this.schemakey] || [];

        const visible_key = [...(this.path || []), this.schemakey].join('.');
        const vis = getVisibility(this, visible_key);
        const icon = vis ? <i class="mdi mdi-menu-down"></i> : <i class="mdi mdi-chevron-right-box-outline"></i>;
        const openclose = <span style={{ cursor: "pointer" }} onClick={() => toggleVisible(this, visible_key)}>{icon}</span>;

        const jsonico = this.asjson ? <i class="mdi mdi-eye-off" title="JSON view"></i> : <i class="mdi mdi-eye" title="JSON view"></i>;
        const jsontgl = <span style={{ cursor: "pointer", color: "gray" }} onClick={() => this.asjson =! this.asjson}>{jsonico}</span>;

        const tdstyle = {
            backgroundColor: "#f8f8f8",
            ...(this.propertygrid ? { minWidth: 0 } : {}),
        };

        if (vis) {
            if (this.asjson || typeof cmodel === 'string') {
                // Allows viewing/editing the raw JSON of the element model
                const rawjson = JSON.stringify(cmodel, null, 4);
                return (
                    <tr>
                        <td style={{ width: "1px", verticalAlign: "top", whiteSpace: "nowrap" }}>
                            {name} {jsontgl} {openclose} {desc}{err}
                        </td>

                        <td class={{ "property-grid": this.propertygrid }} style={{ width: "100%", height: "100px", ...(this.propertygrid ? { minWidth: 0 } : {}) }}>
                            <div ref="arrayRawJsonAceHost" style={{ resize: "vertical", width: "97%", height: this.rawJsonEditorHeight, borderStyle: "solid", borderColor: "silver", borderWidth: "1px", padding: "5px", overflow: "auto", position: "relative" }}>
                                <AceEditor
                                    editorId={'Editor_' + this.uniquekey}
                                    lang="json"
                                    onChangeContent={(text) => this.sync(text)}
                                    content={rawjson}>
                                </AceEditor>
                            </div>
                        </td>

                    </tr>
                );
            }

            const btn_styles = {
                'font-size': "x-small",
            };
            const enabledBtnProps = { unelevated: true, size: "sm", color: "blue-grey", textColor: "white", style: btn_styles, dense: true, class: ['q-px-sm'] };
            const disabledBtnProps = { unelevated: true, size: "sm", color: "grey-5", textColor: "grey-8", style: btn_styles, dense: true, class: ['q-px-sm'] };

            const { allowAdd, allowRemove, allowMove } = this.arrayChromeFlags();

            if (this.canNavigate) {
                const copyCutDisabled = !this.anySelected;
                const cutDisabled = copyCutDisabled || !allowRemove;
                const pasteDisabled = !allowAdd;
                toolbarRow = (
                    <span class={{ "property-grid": this.propertygrid }} style="display:flex; gap: 5px; marginBottom: 6px">
                        <QBtn {...(copyCutDisabled ? disabledBtnProps : enabledBtnProps)} disable={copyCutDisabled} onClick={this.copy}>Copy</QBtn>
                        <QBtn {...(cutDisabled ? disabledBtnProps : enabledBtnProps)} disable={cutDisabled} onClick={this.cut}>Cut</QBtn>
                        <QBtn {...(pasteDisabled ? disabledBtnProps : enabledBtnProps)} disable={pasteDisabled} onClick={this.paste}>Paste</QBtn>

                        <QBtn {...enabledBtnProps} onClick={this.selectAll}>Select All</QBtn>
                        <QBtn {...enabledBtnProps} onClick={this.selectNone}>Select None</QBtn>
                    </span>
                );
            }

            let Tag = utils.schema.getElementType(schema);
            if (!Tag && schema.type == 'object')
                Tag = PropertyGrid;

            if (Tag) {
                this.ensureItemRowKeys(cmodel.length);
                for (let i = 0; i < cmodel.length; i++) {
                    const item = cmodel[i];

                    let select_checkbox;
                    let item_label;
                    let style = {
                        marginBottom: "1px",
                        borderBottomWidth: "1px",
                        borderBottomStyle: "solid",
                        borderBottomColor: "silver",
                        borderRadius: "8px",
                    };

                    if (this.canNavigate) {
                        select_checkbox = <input type="checkbox" checked={this.selected.length > i && this.selected[i]} onChange={(e) => this.setSelection(i, e.target.checked)} />;
                        item_label = (
                            <form-array-label key={i + '_' + (item ? item.$typeSchema : 'null')} cmodel={item} visible={true}>
                            </form-array-label>
                        );
                    }

                    if (item && typeof item === 'object' && !item.$objectId)
                        item.$objectId = utils.generateUUID();

                    const moveUpBtn = allowMove
                        ? <QBtn unelevated flat size="sm" class="q-px-xs" icon="mdi-arrow-up"   textColor={i === 0 ? "grey-5" : "grey-7"} title="Move Up" style={btn_styles} disable={i === 0} onClick={() => this.moveItemUp(i)} />
                        : null;
                    const moveDownBtn = allowMove
                        ? <QBtn unelevated flat size="sm" class="q-px-xs" icon="mdi-arrow-down" textColor={i == cmodel.length - 1 ? "grey-5" : "grey-7"} title="Move Down" style={btn_styles} disable={i == cmodel.length - 1} onClick={() => this.moveItemDown(i)} />
                        : null;
                    const deleteBtn = allowRemove
                        ? <QBtn unelevated flat size="sm" class="q-px-xs" icon="mdi-close"      textColor="grey-7" title="Delete" style={btn_styles} onClick={() => this.removeItem(i)} />
                        : null;
                    const dragHandle = allowMove
                        ? <span v-show={cmodel.length > 1} class="column-drag-handle" style="float:right; padding:0 10px; margin-top: 5px; cursor: grab;">&#x2630;</span>
                        : null;

                    draggableChildren.push(
                        <Draggable key={this.getItemRowKey(i, item)} index={i} style={style}>
                            <span class={{ "draggable-item":true, "property-grid": this.propertygrid }}>
                                {select_checkbox}
                                <Tag
                                    name={'[' + i + ']'}
                                    schemakey={i}
                                    schema={schema}
                                    root={this.root}
                                    cmodel={cmodel}
                                    form_model={this.$attrs.form_model}
                                    child={0}
                                    readonly={schema.readonly || false}
                                    navigateTo={(control, prop, value) => this.navigateTo(control, prop, value || item)}
                                    canNavigate={this.canNavigate}
                                    extractor={(control, prop) => this.extractor(control, prop, i)}
                                    propertygrid={this.propertygrid}
                                >
                                </Tag>
                                {moveUpBtn}
                                {moveDownBtn}
                                {deleteBtn}
                                {dragHandle}
                            </span>
                            <br />{item_label}
                        </Draggable>
                    );
                }
            }

            if (allowAdd) {
                foots.push(
                    <span class={{ "property-grid": this.propertygrid }}>
                        <QBtn {...enabledBtnProps} class={['q-px-sm', 'c-SentenceCaseButton']} no-caps onClick={() => this.addAnother()} icon="mdi-plus"><span>Add Another</span></QBtn>
                    </span>
                );
            }
        }
        else
            heads.push(<span>{cmodel.length} Item(s)</span>);

        return (
            <tr>
                <td style={{ width: "1px", verticalAlign: "top", whiteSpace: "nowrap" }}>
                    {name} {jsontgl} {openclose} {desc}{err}
                </td>
                <td class={{ "property-grid": this.propertygrid }} style={tdstyle}>
                    {heads}
                    {toolbarRow}
                    <Container onDrop={(res) => this.reOrderItems(res)} drag-handle-selector=".column-drag-handle">
                        {draggableChildren}
                    </Container>
                    {foots}
                </td>
            </tr>
        );
    }
});